import {
  createJiraEpic,
  getJiraComponents,
  getJiraConnectUrl,
  getJiraStatus,
  getJiraTeams,
  getJiraVersions,
} from '@/domain/jira/api';
import { type CreateJiraEpicDto, type JiraStatus } from '@/domain/jira/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const JIRA_STATUS_KEY = 'jira-status';
export const JIRA_VERSIONS_KEY = 'jira-versions';
export const JIRA_TEAMS_KEY = 'jira-teams';
export const JIRA_COMPONENTS_KEY = 'jira-components';

export const useJiraStatus = (pollUntilConnected = false) =>
  useQuery({
    queryKey: [JIRA_STATUS_KEY],
    queryFn: getJiraStatus,
    staleTime: 1000 * 60 * 5,
    throwOnError: false,
    refetchInterval: pollUntilConnected
      ? (query: { state: { data?: JiraStatus } }) => (query.state.data?.connected ? false : 2000)
      : false,
  });

export const useJiraVersions = (enabled = true) =>
  useQuery({
    queryKey: [JIRA_VERSIONS_KEY],
    queryFn: getJiraVersions,
    staleTime: 1000 * 60 * 5,
    enabled,
  });

export const useJiraTeams = (enabled = true) =>
  useQuery({
    queryKey: [JIRA_TEAMS_KEY],
    queryFn: getJiraTeams,
    staleTime: 1000 * 60 * 5,
    enabled,
  });

export const useJiraComponents = (enabled = true) =>
  useQuery({
    queryKey: [JIRA_COMPONENTS_KEY],
    queryFn: getJiraComponents,
    staleTime: 1000 * 60 * 5,
    enabled,
  });

export const useCreateJiraEpic = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateJiraEpicDto) => createJiraEpic(dto),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [JIRA_STATUS_KEY] });
    },
  });
};

export const useJiraConnect = () =>
  useMutation({
    mutationFn: getJiraConnectUrl,
  });
